import {
  IconClipboardHeart,
  IconEmergencyBed,
  IconId,
  IconIdBadge2,
  IconMicrophone,
  IconMessageReport,
  IconPills,
  IconTransferIn,
  IconVaccine
} from '@tabler/icons-react';
import { FOLDERS } from '../../constants';
import { rearrangeFolders, getFormattedTime, getAbbreviatedMonth } from '../../helper';
import {
  getPatientProfileDetailsById,
  getGroupbyId,
  getRealmUserInfo,
  calculateTimeAgo
} from '../../services';
import { getDate } from '../../utils';
import {
  EnrichedPatient,
  Patient,
  PatientUserFolder,
  Folder,
  DoctorsTimeline
} from '../../data/patient-management/types';

interface FetchPatientsDetailsArgs {
  patientId: string;
  user_access_token: string;
  generateSignedUrl: (arg: string) => Promise<string>;
  t: (arg: string) => string;
  language: string;
}

const fetchPatientsDetails = async ({
  patientId,
  user_access_token,
  generateSignedUrl,
  t,
  language
}: FetchPatientsDetailsArgs) => {
  const iconMap: Record<string, React.ElementType> = {
    [FOLDERS.PT_DEMOGRAPHICS]: IconClipboardHeart,
    [FOLDERS.MEDICAL_RECORDS]: IconEmergencyBed,
    [FOLDERS.INSURANCE_CARD]: IconId,
    [FOLDERS.ID_CARD]: IconIdBadge2,
    [FOLDERS.AUDIO_FILES]: IconMicrophone,
    [FOLDERS.FEEDBACKS]: IconMessageReport,
    [FOLDERS.RADIOLOGY_IMAGES]: IconMicrophone,
    [FOLDERS.PRESCRIPTIONS]: IconPills,
    [FOLDERS.REFERRALS]: IconTransferIn,
    [FOLDERS.CONSULTATION_NOTES]: IconMessageReport,
    [FOLDERS.VACCINATION]: IconVaccine
  };

  try {
    const result = await getPatientProfileDetailsById({ patientId });

    const patientsList: EnrichedPatient[] = await Promise.all(
      (result?.patientUsers ?? []).map(
        async (patient: Patient): Promise<EnrichedPatient | null> => {
          try {
            const sortedFolders = rearrangeFolders(patient.folderRelation as PatientUserFolder[]);

            // -------------------------------------------
            // 1. Build folders but EXCLUDE ID & INSURANCE
            // -------------------------------------------
            const processedFolders = (sortedFolders ?? [])
              .filter((folder: PatientUserFolder) => {
                const name = folder?.folderName;
                return name !== FOLDERS.ID_CARD && name !== FOLDERS.INSURANCE_CARD;
              })
              .map((folder: PatientUserFolder) => {
                const {
                  folderName,
                  created_at,
                  patientPractiseFiles_aggregate,
                  patientUserFiles_aggregate
                } = folder || {};

                let count = 0;

                if (folderName === FOLDERS.FEEDBACKS || folderName === FOLDERS.PRESCRIPTIONS) {
                  count = patientPractiseFiles_aggregate?.aggregate?.count ?? 0;
                } else if (folderName === FOLDERS.VACCINATION) {
                  count = result?.patientUsers[0]?.vaccinationSubfilesCount?.aggregate?.count || 0;
                } else {
                  count = patientUserFiles_aggregate?.aggregate?.count ?? 0;
                }

                const fileLabel = count === 1 ? t('file') : t('files.label');

                return {
                  title: folderName,
                  files: `${count} ${fileLabel}`,
                  updatedOn: getDate(created_at),
                  icon: iconMap[folderName] || IconClipboardHeart
                };
              });

            // -----------------------------------------------------
            // 2. PRIORITY ORDER for the first 4 folders
            // -----------------------------------------------------
            const PRIORITY_ORDER = [
              FOLDERS.PERSONAL_DATA, // personal data
              FOLDERS.HEALTH_DATA, // health data
              FOLDERS.PRESCRIPTIONS,
              FOLDERS.FEEDBACKS
            ];

            // Extract in specific order
            const priorityFolders = PRIORITY_ORDER.map((key) =>
              processedFolders.find((f: { title: string }) => f.title === key)
            ).filter((f): f is Folder => Boolean(f));

            // Remaining folders (excluding priority ones)
            const remainingFolders = processedFolders.filter(
              (f: { title: string }) => !PRIORITY_ORDER.includes(f.title as string)
            );

            // Final ordered list
            const folders = [...priorityFolders, ...remainingFolders];

            const HIDE_FILE_COUNT = [FOLDERS.PERSONAL_DATA, FOLDERS.HEALTH_DATA];

            const finalFolders = folders.map((folder) => ({
              ...folder,
              files: HIDE_FILE_COUNT.includes(folder.title) ? '' : folder.files
            }));

            const appointmentsData = await Promise.all(
              (patient.Appointments ?? []).map(async (appointment) => {
                const startTime = appointment?.AppointmentSlotRelation?.startTime;
                const endTime = appointment?.AppointmentSlotRelation?.endTime;

                const time =
                  startTime && endTime
                    ? `${getFormattedTime(startTime, language)} - ${getFormattedTime(endTime, language)}`
                    : 'N/A';

                const orgId = appointment?.practiseUserProfileRelation?.organisationId;
                const orgData = await getGroupbyId(orgId, user_access_token);
                const hospital = orgData?.name ?? 'N/A';

                const { firstName, lastName } = await getRealmUserInfo(
                  appointment?.practiseUserProfileRelation?.practiseUserId,
                  user_access_token
                );

                return {
                  date: getAbbreviatedMonth(appointment.date) || 'N/A',
                  time,
                  hospital,
                  status: appointment?.status,
                  doctor: `${firstName ?? ''} ${lastName ?? ''}`.trim() || 'N/A',
                  practiseUserId: appointment?.practiseUserProfileRelation?.practiseUserId || 'N/A'
                };
              })
            );

            const patientUser = await getRealmUserInfo(patient.id, user_access_token);

            const doctorUser = await getRealmUserInfo(patient.reviewerId, user_access_token);
            const doctorsTimeLineRaw = patient.DoctorsTimeline ?? [];
            const doctorsTimeLine = (
              await Promise.all(
                doctorsTimeLineRaw.map(async (timeLine) => {
                  const relation = timeLine?.practiseUserProfileRelation;
                  const practiseUserId = relation?.practiseUserId;
                  if (!practiseUserId) return null;

                  // Get user info
                  let firstName = '',
                    lastName = '';
                  try {
                    const userInfo = await getRealmUserInfo(practiseUserId, user_access_token);
                    firstName = userInfo.firstName;
                    lastName = userInfo.lastName;
                  } catch (err) {
                    console.error('Failed to fetch user info', err);
                    return null;
                  }

                  // Profile image
                  let profileImage = '';
                  try {
                    profileImage = await generateSignedUrl(
                      relation?.practiseUserLivePicRelation?.imageContent
                    );
                  } catch (err) {
                    console.error('Failed to generate image URL', err);
                  }

                  // Hospital
                  let hospital = 'N/A';
                  try {
                    const orgId = relation?.organisationId;
                    const orgData = await getGroupbyId(orgId, user_access_token);
                    hospital = orgData?.name ?? 'N/A';
                  } catch (err) {
                    console.error('Failed to get hospital info', err);
                  }

                  // Department
                  let department = 'N/A';
                  try {
                    const deptId = relation?.departmentId;
                    const deptData = await getGroupbyId(deptId, user_access_token);
                    department = deptData?.name ?? 'N/A';
                  } catch (err) {
                    console.error('Failed to get department info', err);
                  }

                  const prescriptionCount = timeLine?.PrescriptionsCount?.aggregate?.count || 0;
                  const updated =
                    timeLine?.latestPrescription?.[0]?.created_at &&
                    calculateTimeAgo(timeLine.latestPrescription[0].created_at);

                  return {
                    id: practiseUserId,
                    onboardedOn: timeLine?.onboardedOn,
                    lastUpdate: '',
                    name: `${firstName} ${lastName}`,
                    department,
                    profileImage,
                    hospital: {
                      name: hospital,
                      contact: relation?.contact || '',
                      address: relation?.practiseOrganisationRelation?.address || ''
                    },
                    documents: { demographics: 0, progressNotes: 0 },
                    prescriptions: [
                      {
                        doctor: `${firstName} ${lastName}`,
                        count: prescriptionCount,
                        updated
                      }
                    ]
                  };
                })
              )
            ).filter(Boolean); // Remove nulls

            // Count files based on the folders actually shown in the UI
            // Includes: prescriptions, feedbacks, vaccination, and other API folders
            // Excludes: ID card, Insurance, Personal Data, Health Data
            const EXCLUDED_FROM_COUNT = new Set([
              FOLDERS.ID_CARD,
              FOLDERS.INSURANCE_CARD,
              FOLDERS.PERSONAL_DATA,
              FOLDERS.HEALTH_DATA
            ]);

            const dataSafeFilesCount = (sortedFolders ?? []).reduce(
              (sum: number, folder: PatientUserFolder) => {
                const name = folder?.folderName;
                if (!name || EXCLUDED_FROM_COUNT.has(name)) return sum;

                if (name === FOLDERS.FEEDBACKS || name === FOLDERS.PRESCRIPTIONS) {
                  return sum + (folder?.patientPractiseFiles_aggregate?.aggregate?.count ?? 0);
                }

                if (name === FOLDERS.VACCINATION) {
                  return (
                    sum + (result?.patientUsers[0]?.vaccinationSubfilesCount?.aggregate?.count ?? 0)
                  );
                }

                return sum + (folder?.patientUserFiles_aggregate?.aggregate?.count ?? 0);
              },
              0
            );

            return {
              id: patientUser.id,
              profilePicture: await generateSignedUrl(patient?.patientUserDocuments?.imageContent),
              email: patientUser.email,
              patientName: `${patientUser.firstName} ${patientUser.lastName}`,
              doctorName: `${doctorUser.firstName} ${doctorUser.lastName}`,
              dob: patient.DOB,
              gender: patient.gender,
              organisationCount:
                result?.get_patient_connected_organisations_count?.[0]
                  ?.distinct_organisation_count ?? 0,
              doctorsCount: patient.doctorsCount?.aggregate?.count ?? 0,
              dataSafeFoldersCount: Math.max(
                (patient.datasafeFolders?.aggregate?.count ?? 0) - 2,
                0
              ),
              dataSafeFilesCount: dataSafeFilesCount,
              appointmentsCount: patient.appointmentsCount?.aggregate?.count ?? 0,
              appointmentDetails: appointmentsData,
              feedbackCount: patient.FeedbacksCount?.aggregate?.count ?? 0,
              prescriptionCount: patient.PrescriptionsCount?.aggregate?.count ?? 0,
              dataSafeFolders: finalFolders,
              doctorsTimeLine: doctorsTimeLine as DoctorsTimeline[]
            };
          } catch (error) {
            console.warn(`Failed to process patient ID ${patient.id}:`, error);
            return null;
          }
        }
      )
    );

    return {
      patientUsers: patientsList.filter((p): p is EnrichedPatient => p !== null)
    };
  } catch (error) {
    console.log(error);
  }
};

export { fetchPatientsDetails };
